import type { GatorProtectPlan } from '../gatorProtect.types';

export type GetSelectedPlanInfoInput = Partial<Omit<GatorProtectPlan, 'invoice'>> & {
  invoice?: unknown;
  isPro?: boolean;
  gatorProtectHostingCycle?: string;
};

export type SelectedPlanInfo = {
  areDaysExpired: boolean;
  isTrialActive: boolean | undefined;
  isTrialExpired: boolean;
  isProActive: boolean | undefined;
  isProOnMonthlyOrAnnualCycle: boolean;
  isAnnualHostingCycle: boolean;
  isTriennialHostingCycle: boolean;
  isProUpgradeCycleUpsellEligible: boolean;
  isAAClient: boolean;
  trialAvailable: boolean;
  hasClientUsedTrial: boolean | undefined;
  isTurbo: boolean;
  hasInvoice: boolean;
  invoiceId?: unknown;
};


const getAreDaysExpired = (selectedPlan: GetSelectedPlanInfoInput): boolean => {
  if (selectedPlan.expire_days != null) {
    return Number.isInteger(selectedPlan.expire_days) && selectedPlan.expire_days <= 0;
  }
  return false;
};

const emptyPlanInfo: SelectedPlanInfo = {
  areDaysExpired: false,
  isTrialActive: false,
  isTrialExpired: false,
  isProActive: false,
  isProOnMonthlyOrAnnualCycle: false,
  isAnnualHostingCycle: false,
  isTriennialHostingCycle: false,
  isProUpgradeCycleUpsellEligible: false,
  isAAClient: false,
  trialAvailable: false,
  hasClientUsedTrial: false,
  isTurbo: false,
  hasInvoice: false,
};

export const getSelectedPlanInfo = (
  selectedPlan: GetSelectedPlanInfoInput | null | undefined,
): SelectedPlanInfo => {
  if (!selectedPlan) {
    return { ...emptyPlanInfo };
  }

  const areDaysExpired = getAreDaysExpired(selectedPlan);
  const isTrial = Boolean(selectedPlan.is_trial);
  const isTrialActive = isTrial && !areDaysExpired;
  const isTrialExpired = Boolean(isTrial && areDaysExpired);
  const isAAClient = selectedPlan.trial_days === 365;
  const trialAvailable = Boolean(selectedPlan.trial_available);
  const hasClientUsedTrial = selectedPlan.trial_used;
  const isTurbo = Boolean(selectedPlan.shared_hosting_is_turbo);
  const hasInvoice = Boolean(selectedPlan.invoice);
  const isProActive = Boolean(selectedPlan.is_pro);
  const hostingCycle = String(
    selectedPlan.gatorprotect_hosting_cycle ?? selectedPlan.gatorProtectHostingCycle ?? '',
  ).trim().toLowerCase();
  const isProOnMonthlyOrAnnualCycle = Boolean(
    isProActive && (hostingCycle === 'monthly' || hostingCycle === 'annually'),
  );
  const isAnnualHostingCycle = Boolean(isProActive && hostingCycle === 'annually');
  const isTriennialHostingCycle = Boolean(isProActive && hostingCycle === 'triennially');
  const isProUpgradeCycleUpsellEligible = Boolean(
    isProActive && isProOnMonthlyOrAnnualCycle && !isTriennialHostingCycle,
  );

  return {
    areDaysExpired,
    isTrialActive,
    isTrialExpired,
    isProActive,
    isProOnMonthlyOrAnnualCycle,
    isAnnualHostingCycle,
    isTriennialHostingCycle,
    isProUpgradeCycleUpsellEligible,
    isAAClient,
    trialAvailable,
    hasClientUsedTrial,
    isTurbo,
    hasInvoice,
    invoiceId: selectedPlan.invoice,
  };
};
